import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Users, Building2, Shield, Mail, BarChart3, Search, LogOut,
  ArrowLeft, UserCheck, MessageSquare, Send, Clock, CheckCircle2,
  XCircle, Filter, RefreshCw, CreditCard, ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState('pending');
  const [proofModal, setProofModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState('');

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (!u) { navigate('/login'); return; }
    const userData = JSON.parse(u);
    if (userData.user_type !== 'admin') { navigate('/dashboard'); return; }
    loadStats();
    loadUsers();
    loadContacts();
    loadLogs();
    loadPayments();
  }, [navigate]);

  const loadStats = async () => {
    try { const r = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }); setStats(r.data); }
    catch (e) { console.error(e); }
  };
  const loadUsers = async (search = '', type = 'all') => {
    try {
      const params = {};
      if (search) params.search = search;
      if (type && type !== 'all') params.user_type = type;
      const r = await axios.get(`${API}/admin/users`, { headers: getAuthHeaders(), params });
      setUsers(r.data);
    } catch (e) { console.error(e); }
  };
  const loadContacts = async () => {
    try { const r = await axios.get(`${API}/admin/contacts`, { headers: getAuthHeaders() }); setContacts(r.data); }
    catch (e) { console.error(e); }
  };
  const loadLogs = async () => {
    try { const r = await axios.get(`${API}/admin/verification-logs`, { headers: getAuthHeaders() }); setLogs(r.data); }
    catch (e) { console.error(e); }
  };
  const loadPayments = async (status = '') => {
    try {
      const params = status && status !== 'all' ? { status } : {};
      const r = await axios.get(`${API}/admin/payments`, { headers: getAuthHeaders(), params });
      setPayments(r.data);
    } catch (e) { console.error(e); }
  };
  const approvePayment = async (id) => {
    try {
      await axios.post(`${API}/admin/payments/${id}/approve`, {}, { headers: getAuthHeaders() });
      loadPayments(paymentFilter);
      loadStats();
    } catch (e) { alert('Failed to approve.'); }
  };
  const rejectPayment = async (id) => {
    const notes = prompt('Reason for rejection (sent to user):') || '';
    try {
      await axios.post(`${API}/admin/payments/${id}/reject`, { notes }, { headers: getAuthHeaders() });
      loadPayments(paymentFilter);
    } catch (e) { alert('Failed to reject.'); }
  };

  useEffect(() => { loadPayments(paymentFilter); }, [paymentFilter]);

  const handleSendReminders = async () => {
    setSendingReminders(true);
    setReminderResult('');
    try {
      const r = await axios.post(`${API}/admin/send-reminders`, {}, { headers: getAuthHeaders() });
      setReminderResult(r.data.message);
    } catch (e) { setReminderResult('Failed to send reminders.'); }
    finally { setSendingReminders(false); }
  };

  useEffect(() => { loadUsers(searchTerm, userTypeFilter); }, [searchTerm, userTypeFilter]);

  const handleLogout = () => { localStorage.removeItem('user'); localStorage.removeItem('token'); navigate('/login'); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-meritum-navy text-white py-6">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between mb-2">
            <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white font-body text-sm"><ArrowLeft size={16} />Home</Link>
            <Button onClick={handleLogout} variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 font-body" data-testid="admin-logout"><LogOut size={16} className="mr-2" />Logout</Button>
          </div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-3"><Shield size={24} className="text-meritum-gold" />Admin Dashboard</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-600 bg-blue-50' },
              { label: 'Candidates', value: stats.candidates, icon: UserCheck, color: 'text-emerald-600 bg-emerald-50' },
              { label: 'Organizations', value: stats.organizations, icon: Building2, color: 'text-violet-600 bg-violet-50' },
              { label: 'Contact Forms', value: stats.contacts, icon: Mail, color: 'text-amber-600 bg-amber-50' },
              { label: 'Invitations', value: stats.invitations, icon: Send, color: 'text-cyan-600 bg-cyan-50' },
              { label: 'Verified Refs', value: stats.confirmed_refs, icon: CheckCircle2, color: 'text-green-600 bg-green-50' },
              { label: 'Messages', value: stats.messages_total, icon: MessageSquare, color: 'text-rose-600 bg-rose-50' },
              { label: 'Pending Pay', value: stats.payments_pending, icon: CreditCard, color: 'text-orange-600 bg-orange-50' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="bg-white rounded-xl shadow-sm p-4 border border-slate-100">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${color}`}><Icon size={18} /></div>
                <p className="font-heading text-2xl font-bold text-meritum-navy">{value}</p>
                <p className="font-body text-xs text-slate-500">{label}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="users">
          <TabsList className="mb-6 bg-white shadow-sm">
            <TabsTrigger value="users" className="font-body"><Users size={16} className="mr-2" />Users</TabsTrigger>
            <TabsTrigger value="contacts" className="font-body"><Mail size={16} className="mr-2" />Contact Submissions</TabsTrigger>
            <TabsTrigger value="verification" className="font-body"><Shield size={16} className="mr-2" />Verification Logs</TabsTrigger>
            <TabsTrigger value="payments" className="font-body" data-testid="admin-tab-payments"><CreditCard size={16} className="mr-2" />Payments</TabsTrigger>
            <TabsTrigger value="reminders" className="font-body"><Clock size={16} className="mr-2" />Reminders</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by name or email..." className="pl-10" data-testid="admin-user-search" />
                </div>
                <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
                  <SelectTrigger className="w-44" data-testid="admin-user-type-filter"><Filter size={16} className="mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="candidate">Candidates</SelectItem>
                    <SelectItem value="organization">Organizations</SelectItem>
                    <SelectItem value="admin">Admins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead><tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 font-heading text-meritum-navy">Name</th>
                    <th className="text-left py-3 px-4 font-heading text-meritum-navy">Email</th>
                    <th className="text-left py-3 px-4 font-heading text-meritum-navy">Type</th>
                    <th className="text-left py-3 px-4 font-heading text-meritum-navy">Status</th>
                    <th className="text-left py-3 px-4 font-heading text-meritum-navy">Joined</th>
                  </tr></thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-3 px-4 font-body font-medium text-meritum-navy">{u.name}</td>
                        <td className="py-3 px-4 font-body text-slate-600">{u.email}</td>
                        <td className="py-3 px-4"><span className={`px-2 py-1 rounded text-xs font-medium ${u.user_type === 'candidate' ? 'bg-emerald-100 text-emerald-700' : u.user_type === 'organization' ? 'bg-violet-100 text-violet-700' : 'bg-meritum-gold/20 text-meritum-gold'}`}>{u.user_type}</span></td>
                        <td className="py-3 px-4"><span className={`px-2 py-1 rounded text-xs ${u.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.is_active !== false ? 'Active' : 'Inactive'}</span></td>
                        <td className="py-3 px-4 font-body text-slate-400 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="font-body text-sm text-slate-400 mt-4">{users.length} users</p>
              </div>
            </div>
          </TabsContent>

          {/* Contact Submissions Tab */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-heading text-lg font-semibold text-meritum-navy mb-4">Contact Form Submissions</h3>
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} className="p-4 border border-slate-100 rounded-lg hover:bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="font-heading text-sm font-semibold text-meritum-navy">{c.name}</span>
                        <span className="font-body text-xs text-slate-400">{c.email}</span>
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">{c.user_type || 'general'}</span>
                      </div>
                      <span className="font-body text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    {c.subject && <p className="font-body text-sm font-medium text-meritum-navy">{c.subject}</p>}
                    <p className="font-body text-sm text-slate-600 mt-1">{c.message}</p>
                  </div>
                ))}
                {contacts.length === 0 && <p className="font-body text-slate-400 text-center py-8">No contact submissions yet.</p>}
              </div>
            </div>
          </TabsContent>

          {/* Verification Logs Tab */}
          <TabsContent value="verification">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-heading text-lg font-semibold text-meritum-navy mb-4">Verification Activity</h3>
              <div className="space-y-3">
                {logs.map((log, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 border border-slate-100 rounded-lg">
                    {log.action === 'invitation_sent' && <Send size={16} className="text-blue-500 flex-shrink-0" />}
                    {log.action === 'reference_confirmed' && <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />}
                    {log.action === 'reference_declined' && <XCircle size={16} className="text-red-500 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm text-meritum-navy">
                        {log.action === 'invitation_sent' && `Invite sent to ${log.details?.referee_name} for "${log.details?.skill_name}"`}
                        {log.action === 'reference_confirmed' && `${log.details?.referee_name} confirmed "${log.details?.skill_name}"`}
                        {log.action === 'reference_declined' && `${log.details?.referee_name} declined "${log.details?.skill_name}"`}
                      </p>
                      <p className="font-body text-xs text-slate-400">Candidate: {log.candidate_id?.slice(0, 8)}...</p>
                    </div>
                    <span className="font-body text-xs text-slate-400 flex-shrink-0">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</span>
                  </div>
                ))}
                {logs.length === 0 && <p className="font-body text-slate-400 text-center py-8">No verification activity yet.</p>}
              </div>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="bg-white rounded-xl shadow-sm p-6" data-testid="admin-payments-panel">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-semibold text-meritum-navy flex items-center gap-2"><CreditCard size={18} className="text-orange-600" />Payment Submissions</h3>
                <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                  <SelectTrigger className="w-44" data-testid="admin-payment-filter"><Filter size={14} className="mr-2" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {payments.length === 0 ? (
                <p className="font-body text-slate-400 text-center py-8">No payment submissions found.</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((p) => (
                    <div key={p.id} className="border border-slate-100 rounded-lg p-4 hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-heading text-sm font-semibold text-meritum-navy">{p.user_name}</span>
                            <span className="font-body text-xs text-slate-400">{p.user_email}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              p.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                              p.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                            }`}>{p.status}</span>
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium uppercase">{p.method}</span>
                          </div>
                          <p className="font-body text-sm text-slate-600">
                            <strong>R{p.amount}</strong> · {p.plan_type} plan
                            {p.reference && <> · ref: <code className="text-xs">{p.reference}</code></>}
                          </p>
                          <p className="font-body text-xs text-slate-400 mt-1">Submitted {new Date(p.submitted_at).toLocaleString()}</p>
                          {p.admin_notes && <p className="font-body text-xs text-red-600 mt-1">Note: {p.admin_notes}</p>}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {p.proof_image && (
                            <Button onClick={() => setProofModal(p.proof_image)} size="sm" variant="outline" className="border-slate-300 font-body" data-testid={`view-proof-${p.id}`}><ImageIcon size={14} className="mr-1" />Proof</Button>
                          )}
                          {p.status === 'pending' && (
                            <>
                              <Button onClick={() => approvePayment(p.id)} size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700 font-body" data-testid={`approve-payment-${p.id}`}><CheckCircle2 size={14} className="mr-1" />Approve</Button>
                              <Button onClick={() => rejectPayment(p.id)} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 font-body" data-testid={`reject-payment-${p.id}`}><XCircle size={14} className="mr-1" />Reject</Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {proofModal && (
              <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setProofModal(null)} data-testid="proof-modal">
                <div className="bg-white rounded-xl max-w-3xl max-h-[90vh] overflow-auto p-2">
                  <img src={proofModal} alt="Proof of payment" className="max-w-full" />
                </div>
              </div>
            )}
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders">
            <div className="bg-white rounded-xl shadow-sm p-6 max-w-xl">
              <h3 className="font-heading text-lg font-semibold text-meritum-navy mb-2">Monthly Profile Reminders</h3>
              <p className="font-body text-sm text-slate-600 mb-6">Send reminder emails to candidates who haven't updated their profile in the last 30 days.</p>
              <Button onClick={handleSendReminders} disabled={sendingReminders} className="bg-meritum-teal text-white hover:bg-meritum-navy font-body" data-testid="send-reminders-btn">
                {sendingReminders ? <><RefreshCw size={16} className="mr-2 animate-spin" />Sending...</> : <><Send size={16} className="mr-2" />Send Reminder Emails</>}
              </Button>
              {reminderResult && <p className="font-body text-sm text-emerald-600 mt-3" data-testid="reminder-result">{reminderResult}</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
